import { decodeHTML } from "entities";

// Tags whose text content (and inline formatting) we keep when emitted by AI
// rich-text. Anything outside this list is unwrapped so its safe text stays.
const SANITIZE_ALLOWED_TAGS = new Set([
  "p",
  "br",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "s",
  "strike",
  "sub",
  "sup",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "ul",
  "ol",
  "li",
  "blockquote",
  "pre",
  "code",
  "hr",
  "span",
  "div",
  "a",
  "img",
  "table",
  "thead",
  "tbody",
  "tfoot",
  "tr",
  "th",
  "td"
]);

// Tags we drop entirely (subtree included). Active-content vectors and tags
// that can pivot to scripting via attributes (svg/onload, math, srcdoc, etc).
const SANITIZE_DANGEROUS_TAGS = new Set([
  "script",
  "style",
  "iframe",
  "object",
  "embed",
  "form",
  "input",
  "button",
  "textarea",
  "select",
  "link",
  "meta",
  "base",
  "svg",
  "math",
  "audio",
  "video",
  "source",
  "track",
  "picture"
]);

const SANITIZE_DROP_ATTRS = new Set(["srcdoc", "formaction", "xlink:href"]);

const SANITIZE_ALLOWED_STYLES = new Set([
  "color",
  "font-family",
  "font-size",
  "font-style",
  "font-weight",
  "width",
  "height",
  "max-width",
  "max-height"
]);

// Void elements are written as `<tag/>` even if they came in unclosed.
// `img` is in `SANITIZE_ALLOWED_TAGS` and gated by `isSafeUrl` for its `src`.
const SANITIZE_VOID_ELEMENTS = new Set([
  "br",
  "hr",
  "img",
  "input",
  "area",
  "base",
  "col",
  "embed",
  "link",
  "meta",
  "param",
  "source",
  "track",
  "wbr"
]);

// Strip HTML comments, processing instructions, CDATA, and DOCTYPE blocks.
// These cannot legitimately appear in AI-generated rich text and have been
// used historically as comment-eating XSS tricks. Split into separate REs
// to keep each one simple.
const SANITIZE_COMMENT_RE = /<!--[\s\S]*?-->/g;
const SANITIZE_PI_RE = /<\?[\s\S]*?\?>/g;
const SANITIZE_CDATA_RE = /<!\[CDATA\[[\s\S]*?\]\]>/g;
const SANITIZE_DOCTYPE_RE = /<!DOCTYPE[^>]*>/gi;

// Match an attribute inside a tag's attribute blob:
//   name="value" | name='value' | name=value | name (no value)
// Capture groups: 1=name, 2=double-quoted, 3=single-quoted, 4=unquoted.
const SANITIZE_ATTR_RE =
  /([a-zA-Z_:][\w:.-]*)\s*(?:=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>=`]+)))?/g;

// Match any tag: open `<tag attrs>`, close `</tag>`, or self-closing `<tag/>`.
// Capture groups: 1=slash (close tag), 2=name, 3=attrs blob, 4=trailing slash.
const SANITIZE_TAG_RE =
  /<(\/?)\s*([a-zA-Z][a-zA-Z0-9]*)\b([^>]*?)(\/?)\s*>/g;

function isSafeUrl(value: string | null): boolean {
  if (value == null) return false;
  const v = value.trim().toLowerCase();
  // eslint-disable-next-line no-script-url
  if (v.startsWith("javascript:") || v.startsWith("vbscript:")) return false;
  // Allow data: only for images; block data:text/html and friends.
  if (v.startsWith("data:") && !v.startsWith("data:image/")) return false;
  return true;
}

function escapeAttrValue(value: string): string {
  // Escape `&` first (else we'd double-encode the entities introduced below),
  // then quotes (which break out of attribute delimiters), then angle brackets
  // for defense-in-depth against downstream regex parsers and copy-paste into
  // unquoted contexts.
  return value
    .replaceAll("&", "&amp;")
    .replaceAll('"', "&quot;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");
}

function filterStyleAttr(styleValue: string): string {
  return styleValue
    .split(";")
    .map((s) => s.trim())
    .filter((s) => {
      if (!s) return false;
      const [property] = s.split(":").map((x) => x.trim());
      return SANITIZE_ALLOWED_STYLES.has(property.toLowerCase());
    })
    .join("; ");
}

function cleanAttribute(rawName: string, rawValue: string): string | null {
  const name = rawName.toLowerCase();
  if (name.startsWith("on")) return null;
  if (SANITIZE_DROP_ATTRS.has(name)) return null;

  // Decode entities so url validation isn't bypassed by `&#x6A;avascript:`.
  const decoded = decodeHTML(rawValue);

  if ((name === "href" || name === "src") && !isSafeUrl(decoded)) return null;

  if (name === "style") {
    const filtered = filterStyleAttr(decoded);
    return filtered ? `${name}="${escapeAttrValue(filtered)}"` : null;
  }

  return `${name}="${escapeAttrValue(decoded)}"`;
}

function cleanOpenTag(
  tagName: string,
  attrsBlob: string,
  selfClosing: boolean
): string {
  if (!SANITIZE_ALLOWED_TAGS.has(tagName)) return "";

  let cleanedAttrs: string[] = [];
  let m: RegExpExecArray | null;
  SANITIZE_ATTR_RE.lastIndex = 0;
  m = SANITIZE_ATTR_RE.exec(attrsBlob);
  while (m !== null) {
    const value = m[2] ?? m[3] ?? m[4] ?? "";
    const cleaned = cleanAttribute(m[1], value);
    if (cleaned) cleanedAttrs.push(cleaned);
    m = SANITIZE_ATTR_RE.exec(attrsBlob);
  }

  // Anchor hardening: drop any user-supplied `rel` and force the safe value.
  // Replacement (rather than token merging) is intentional — AI-generated
  // content has no legitimate need for custom rel tokens, and replacement
  // guarantees `noopener noreferrer` is present so window.opener tabnabbing
  // and referrer leaks are blocked even when `target="_blank"` is used.
  if (tagName === "a") {
    cleanedAttrs = cleanedAttrs.filter((a) => !a.startsWith("rel="));
    cleanedAttrs.push(`rel="noopener noreferrer"`);
  }

  const close =
    selfClosing || SANITIZE_VOID_ELEMENTS.has(tagName) ? "/" : "";
  const attrs = cleanedAttrs.length ? ` ${cleanedAttrs.join(" ")}` : "";
  return `<${tagName}${attrs}${close}>`;
}

function stripDangerousBlocks(html: string): string {
  let out = html;
  for (const tag of SANITIZE_DANGEROUS_TAGS) {
    // Non-greedy match of paired tags, including their content. Loop until
    // fixed-point so nested duplicates like <iframe><iframe>x</iframe></iframe>
    // are fully removed.
    const blockRe = new RegExp(
      String.raw`<${tag}\b[\s\S]*?</${tag}\s*>`,
      "gi"
    );
    let prev: string;
    do {
      prev = out;
      out = out.replace(blockRe, "");
    } while (out !== prev);
  }
  return out;
}

/**
 * Sanitize an HTML string into an allowlisted, attribute-scrubbed form safe
 * for rendering through `dangerouslySetInnerHTML` or pasting into TinyMCE.
 *
 * Pure-regex implementation (no `DOMParser`, no `<template>`). The trade-off
 * is well known: regex-based HTML sanitizers can be bypassed with sufficiently
 * malformed input. This implementation defends against the common attack
 * vectors (script blocks, event handlers, dangerous URL schemes, srcdoc) and
 * is intended for content that is already approximately well-formed — AI
 * backend output and contentEditable-normalized HTML. Do not use it on
 * arbitrary attacker-supplied raw HTML.
 */
export function sanitizeHTML(html: string): string {
  if (!html) return "";

  // 1) Strip noise that can hide tags from the rest of the pipeline.
  let out = html
    .replace(SANITIZE_COMMENT_RE, "")
    .replace(SANITIZE_PI_RE, "")
    .replace(SANITIZE_CDATA_RE, "")
    .replace(SANITIZE_DOCTYPE_RE, "");

  // 2) Remove paired dangerous tags WITH their content.
  out = stripDangerousBlocks(out);

  // 3) Walk every remaining tag: clean allowlisted ones, drop everything
  //    outside the allowlist (close tags + orphan/self-closing dangerous
  //    tags fall through to the empty-string branch).
  out = out.replace(
    SANITIZE_TAG_RE,
    (_match, slash: string, rawName: string, attrs: string, selfClose: string) => {
      const name = rawName.toLowerCase();
      if (SANITIZE_DANGEROUS_TAGS.has(name)) return "";
      if (slash) return SANITIZE_ALLOWED_TAGS.has(name) ? `</${name}>` : "";
      return cleanOpenTag(name, attrs, Boolean(selfClose));
    }
  );

  return out;
}
